// Single source of truth for discount/specials logic, shared by the printed menu and the
// consumer storefront (apps/marketplace/src/hooks/use-product-pricing). Do not fork this file
// again -- see specials-utils.test.ts for the behavior both callers depend on.
//
// Function bodies are byte-for-byte identical to marketplace's original specials.utils.ts.
// Only the types were substituted for local structural equivalents (this package cannot import
// from apps/marketplace), and `brand` access was made optional-safe because the printed menu's
// raw product data does not guarantee one.
import _ from 'lodash';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — products.js has ~80 untyped exports; a partial .d.ts breaks other consumers
import { getProductWeight, saleSatisfiesWeightCondition } from 'shared/helpers/products';

export type MenuType = 'med' | 'rec';

enum DiscountType {
  dollarDiscount = 'dollarDiscount',
  percentDiscount = 'percentDiscount',
  targetPrice = 'targetPrice',
}

export type SpecialRestriction = {
  appliesToAnyWeight: boolean;
  discount?: number;
  discountType?: string;
  isPercentDiscount: boolean;
  weight?: number;
  weights?: number[];
  weightOperator?: 'equalTo' | 'greaterThan' | 'greaterThanEqualTo';
};

// `| null` throughout matches GraphQL codegen's Maybe<T> = T | null, so both plain POJOs and
// marketplace's generated types satisfy these without call-site casts. Type-only relaxation;
// no runtime effect.
type Nullable<T> = T | null | undefined;

// Structural stand-in for marketplace's generated `Products_specialData` GraphQL type (only the
// fields this file reads).
type SpecialData = {
  saleSpecials?: Nullable<
    Array<
      Nullable<{
        discount?: Nullable<number>;
        eligibleProductOptions?: Nullable<Array<Nullable<string>>>;
        menuType?: Nullable<string>;
        paymentRestrictions?: Nullable<{ payByBankSignupIncentive?: Nullable<boolean> }>;
        percentDiscount?: Nullable<boolean>;
        saleDiscounts?: Nullable<
          Array<
            Nullable<{
              discountType?: Nullable<string>;
              exclusions?: Nullable<Array<Nullable<{ weights?: Nullable<Array<Nullable<string>>> }>>>;
              weights?: Nullable<Array<Nullable<string>>>;
            }>
          >
        >;
        specialRestrictions?: Nullable<
          Record<
            string,
            Nullable<{
              discountType?: Nullable<string>;
              weight?: Nullable<string>;
              weights?: Nullable<Array<Nullable<string>>>;
              weightOperator?: Nullable<'equalTo' | 'greaterThan' | 'greaterThanEqualTo'>;
            }>
          >
        >;
      }>
    >
  >;
};

// Structural stand-in for marketplace's generated `Product` type (only the fields this file
// reads).
type ProductForSpecialsKeys = {
  _id?: Nullable<string>;
  // Optional (unlike marketplace's own Product type, where brand is always present from its
  // GraphQL schema): the printed menu's raw product data does not guarantee a brand exists.
  brand?: Nullable<{ id?: Nullable<string>; name?: Nullable<string> }>;
  brandId?: Nullable<string>;
  brandName?: Nullable<string>;
  enterpriseProductId?: Nullable<string>;
  id?: Nullable<string>;
  subcategory?: Nullable<string>;
  type?: Nullable<string>;
};

export function getIsSpecialApplicable(weight: string, specialRestrictions: SpecialRestriction[]): boolean {
  const weightValue = getProductWeight(weight);
  return specialRestrictions.some((special) =>
    saleSatisfiesWeightCondition(
      weightValue,
      special.weightOperator,
      special.weights ?? special.weight,
      special.appliesToAnyWeight
    )
  );
}

export type TransformSpecialRestrictionsProps = {
  menuType: MenuType;
  productSpecialsKeys: Set<string>;
  specialData: SpecialData | null | undefined;
};

export function transformSpecialRestrictions({
  menuType,
  productSpecialsKeys,
  specialData,
}: TransformSpecialRestrictionsProps): SpecialRestriction[] {
  const fullMenuType = { med: `medical`, rec: `recreational` };
  const restrictions: SpecialRestriction[] = [];

  if (!Array.isArray(specialData?.saleSpecials)) {
    return restrictions;
  }

  // we filter out PBB incentives so they don't interfere with weight tile pricing
  const filteredSpecials = specialData?.saleSpecials?.filter(
    (special) => !special?.paymentRestrictions?.payByBankSignupIncentive
  );

  if (!filteredSpecials) {
    return restrictions;
  }

  filteredSpecials.forEach((special) => {
    // specials v3.5 support
    if (!_.isEmpty(special?.saleDiscounts)) {
      const saleDiscount = special?.saleDiscounts?.[0];
      if (
        !_.isEmpty(special?.eligibleProductOptions) &&
        _.includes(['both', menuType, fullMenuType[menuType]], special?.menuType)
      ) {
        restrictions.push({
          appliesToAnyWeight:
            _.isEmpty(saleDiscount?.weights) &&
            _.isEmpty(_.flatMap(saleDiscount?.exclusions, (exclusion) => exclusion?.weights ?? [])),
          discountType: saleDiscount?.discountType as DiscountType,
          isPercentDiscount: saleDiscount?.discountType === 'percentDiscount',
          weights: special?.eligibleProductOptions?.map((weight) => getProductWeight(weight)),
          weightOperator: 'equalTo',
        });
      }
      // < 3.5 specials
    } else if (special?.specialRestrictions) {
      const specialRestrictions = special.specialRestrictions ?? {};
      const discount = special.discount ?? undefined;
      const isPercentDiscount = special.percentDiscount ?? false;

      Object.entries(specialRestrictions).forEach((item: [string, any]) => {
        const [key, { weight, weights, weightOperator, discountType }] = item;

        const parsedWeights =
          weights?.length > 0
            ? weights?.map((currentWeight: string) => getProductWeight(currentWeight))
            : [getProductWeight(weight)];

        if (productSpecialsKeys.has(key) && _.includes(['both', menuType, fullMenuType[menuType]], special.menuType)) {
          restrictions.push({
            appliesToAnyWeight: !weight && !weights && !weightOperator,
            discount,
            discountType,
            isPercentDiscount,
            weight: weight && getProductWeight(weight),
            weights: parsedWeights,
            weightOperator,
          });
        }
      });
    }
  });

  return restrictions;
}

export function getProductSpecialsKeys({
  brandId,
  brand,
  brandName,
  type,
  subcategory,
  _id,
  id,
  enterpriseProductId,
}: ProductForSpecialsKeys): Set<string> {
  // enterpriseProductId-restricted specials key restrictions by the enterprise product id, in either the
  // `EPID_<id>` prefixed form or the bare `<id>` form. This mirrors the server-side matching in
  // apps/api/src/graphql/resolvers/product/serializer/stages/specials.js (getKey / calculateSaleSpecialPricesForProduct).
  const productSpecialsKeys = [
    brandId ?? brand?.id,
    brandName ?? brand?.name,
    type,
    subcategory,
    _id ?? id,
    enterpriseProductId ? `EPID_${enterpriseProductId}` : null,
    enterpriseProductId,
  ];
  const productSpecialsKeysSet = new Set<string>();

  productSpecialsKeys.forEach((key) => {
    if (typeof key === 'string') {
      productSpecialsKeysSet.add(key);
    }
  });

  return productSpecialsKeysSet;
}

export const calcDiscountPercentage = (specialPrice: number, price: number): number =>
  Math.round((1 - specialPrice / price) * 100);
